import React from 'react'

import {
  Body2,
  Box,
  Color,
  Display,
  FontWeight,
  Join,
  Space,
  withCitrusTheme,
} from '@gousto-internal/citrus-react'

import { useMarketingPricingConfig } from '@library/api-pricing'

import { client as clientRoutes } from 'config/routes'
import { useIsCwcHomepageEnabled } from 'routes/Home/hooks/useIsCwcHomepageEnabled'
import { useLocation } from 'routes/Menu/domains/collections/internal/useLocation'

export const terms = `Not including additional charges. Offer may only be claimed once and applies to the account you are logged into. Once claimed, the discount applies to your first 6 orders: 50% off your first box, 40% off your second box and 20% off your 3rd to 6th boxes, provided each order is delivered within 45 days of your first delivery. This offer cannot be used in conjunction with other offers.`

// White, bold, underlined inline legal links; hover re-asserts white to beat Bootstrap's global a:hover.
const NoticeLink = withCitrusTheme('a', (theme) => ({
  color: theme.color(Color.White),
  fontWeight: FontWeight.Bold,
  textDecoration: 'underline',
  '&:hover, &:focus': {
    color: theme.color(Color.White),
  },
}))

type Props = {
  children?: React.ReactNode
}

export const FooterNotice = ({ children }: Props) => {
  const existingPricing = {
    portionDescription: '5 x 4',
    pricePerPortion: '3.09',
    discountedPricePerPortion: '1.54',
  }

  const newPricing = useMarketingPricingConfig()

  const { portionDescription, pricePerPortion, discountedPricePerPortion } =
    newPricing || existingPricing

  const { pathname } = useLocation()
  const isHomePage = pathname === clientRoutes.home
  const isCwcHomepageEnabled = useIsCwcHomepageEnabled()
  const showCwcFooterContent = isHomePage && isCwcHomepageEnabled

  // Keep as arrays, not Fragments — Join only separates direct children.
  const freeNextDayDeliveryNotice = (key: string) => (
    <Body2 key={key} color={Color.White}>
      <sup>†</sup> Free Next Day Delivery is available for new customers who complete the sign-up
      and checkout process before 11:59am on Friday, Saturday, Sunday, Monday, and Tuesday. Next Day
      Delivery availability is limited and so where capacity is reached, customers may see a later
      first delivery date available. Next day delivery is not available across all regions
      (including Northern Ireland) in the UK and is subject to geographical availability.
    </Body2>
  )

  const cwcFooterParagraphs = [
    <Body2 key="cwc-price" color={Color.White}>
      <sup>*</sup> £{pricePerPortion} Based on price per portion on a {portionDescription}{' '}
      subscription box. Excl. £3.99 delivery fee & surcharges. £{discountedPricePerPortion} based on
      new customer price per portion on a {portionDescription} subscription box. Excl. surcharges.
    </Body2>,
    freeNextDayDeliveryNotice('cwc-delivery'),
    <Body2 key="cwc-comparison" color={Color.White}>
      <sup>‡</sup> Based on Gousto having more recipes available on the weekly menu than other
      recipe boxes (see{' '}
      <Box display={Display.Inline} style={{ fontWeight: FontWeight.Bold }}>
        <NoticeLink href="https://cook.gousto.co.uk/choice/">verification of choice</NoticeLink>
      </Box>
      ) and a lower price-per-portion than other recipe boxes (see{' '}
      <Box display={Display.Inline} style={{ fontWeight: FontWeight.Bold }}>
        <NoticeLink href="https://cook.gousto.co.uk/best-value/">verification of value</NoticeLink>
      </Box>
      )
    </Body2>,
  ]

  const existingFooterParagraphs = [
    <Body2 key="existing-terms" color={Color.White}>
      ** New customers only.{' '}
      <Box display={Display.Inline} style={{ fontWeight: FontWeight.Bold }}>
        <NoticeLink href="https://www.gousto.co.uk/terms-and-conditions">
          Standard T&Cs apply.
        </NoticeLink>
      </Box>
      {terms}
      <br />
    </Body2>,
    <Body2 key="existing-comparison" color={Color.White}>
      * Based on Gousto having more recipes, and more quick & easy recipes, available on the weekly
      menu than other recipe boxes delivering to the same regions in the UK as Gousto. The full
      details of this comparison can be found here:{' '}
      <Box display={Display.Inline} style={{ fontWeight: FontWeight.Bold }}>
        <NoticeLink href="https://cook.gousto.co.uk/choice/">verification of choice</NoticeLink>
      </Box>{' '}
      and our{' '}
      <Box display={Display.Inline} style={{ fontWeight: FontWeight.Bold }}>
        <NoticeLink href="https://cook.gousto.co.uk/more-quick-and-easy-recipes/">
          biggest ever menu.
        </NoticeLink>
      </Box>
    </Body2>,
    freeNextDayDeliveryNotice('existing-delivery'),
    <Body2 key="existing-price" color={Color.White}>
      <sup>‡</sup> £{pricePerPortion} Based on price per portion on a {portionDescription}{' '}
      subscription box. Excl. £3.99 delivery fee & surcharges. £{discountedPricePerPortion} based on
      new customer price per portion on a {portionDescription} subscription box. Excl. surcharges.
    </Body2>,
  ]

  return (
    <Box paddingBottom={4}>
      <Join with={<Space size={2} />}>
        {children}
        {showCwcFooterContent ? cwcFooterParagraphs : existingFooterParagraphs}
      </Join>
    </Box>
  )
}
